import { createRef, VFC, useCallback } from "react";
import { EditableNode, NodeBox } from "./editable-node";
import { css } from "src/styles/theme";
import { EditorState } from "../types/editor-state.types";
import { editorGrid } from "../editor.constants";
import { EditorAction } from "../types/editor-action.types";
import { useTree } from "src/features/tree";
import { useGridPositions } from "../hooks/use-grid-positions.hook";
import { InfiniteCanvas } from "./infinite-canvas";
import { DRAG_HANDLE_CLASS } from "./node-dragger";
import { InfiniteCanvasView } from "../types/canvas.types";
import { createActionToMoveNodeIntoView } from "../helpers/canvas.helpers";
import Draggable, { DraggableData } from "react-draggable";

export interface EditorCanvasProps {
  state: EditorState;
  dispatch: (action: EditorAction) => void;
}

export const EditorCanvas: VFC<EditorCanvasProps> = ({ state, dispatch }) => {
  const canvasRef = createRef<HTMLDivElement>();
  const tree = useTree(state.positions, state.nodes);
  const gridPositions = useGridPositions(state.positions);

  const updateCanvasView = useCallback(
    (view: InfiniteCanvasView) => {
      dispatch({ type: "UPDATE_CANVAS_VIEW", view });
    },
    [dispatch]
  );

  const onStopHandler = (nodeId: string, data: DraggableData) => {
    const x = Math.floor(data.lastX / editorGrid.x);
    const y = Math.floor(data.lastY / editorGrid.y);
    dispatch({ type: "UPDATE_POSITION", id: nodeId, x, y });
  };

  const scrollIntoView = (nodeBox: NodeBox) => {
    const action = createActionToMoveNodeIntoView({
      canvasWidth: canvasRef.current!.clientWidth,
      canvasHeight: canvasRef.current!.clientHeight,
      canvasViewX: state.canvasView.x,
      canvasViewY: state.canvasView.y,
      nodeWidth: nodeBox.width,
      nodeHeight: nodeBox.height,
      nodeX: nodeBox.x * editorGrid.x,
      nodeY: nodeBox.y * editorGrid.y,
    });

    action && dispatch(action);
  };

  return (
    <InfiniteCanvas
      ref={canvasRef}
      view={state.canvasView}
      onChange={updateCanvasView}
    >
      {tree.nodes.map((treeNode) => (
        <Draggable
          key={treeNode.node.id}
          position={gridPositions[treeNode.node.id]}
          onStop={(_, data) => onStopHandler(treeNode.node.id, data)}
          grid={[editorGrid.x, editorGrid.y]}
          handle={`.${DRAG_HANDLE_CLASS}`}
          defaultClassName={$draggable().toString()}
        >
          <div style={{ height: `${editorGrid.nodeHeight}px` }}>
            <EditableNode
              node={treeNode.node}
              isActive={!!treeNode.isActive}
              focused={state.focusedNodeId === treeNode.node.id}
              dispatch={dispatch}
              position={state.positions[treeNode.node.id]}
              scrollIntoView={scrollIntoView}
            />
          </div>
        </Draggable>
      ))}
    </InfiniteCanvas>
  );
};

const $draggable = css({
  position: "absolute",
  "&.react-draggable-dragging": {
    zIndex: 9999,
  },
});
